/**
 *
 *  Header Menu
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 02/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useState } from "react";
// import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import { useAppState } from "../../../state";

// Components
import Dropdown from "../../Dropdown/Dropdown";
import ProfilePicture from "../../ProfilePicture/ProfilePicture";

// Hooks
import useTheme from "../../ThemeProvider/useTheme/useTheme";

// Icons
import MenuBurgerIcon from "../../../icons/MenuBurgerIcon";
import CaretRightIcon from "../../../icons/CaretRightIcon";
import CupIcon from "../../../icons/CupIcon";
import BoltIcon from "../../../icons/BoltIcon";
import UserIcon from "../../../icons/UserIcon";
import LoginIcon from "../../../icons/LoginIcon";
import CalendarIcon from "../../../icons/CalendarTruBeIcon";
import SpeechBalloonIcon from "../../../icons/SpeechBalloonIcon";
import EquipmentIcon from "../../../icons/EquipmentIcon";
import TruBeIcon from "../../../icons/TruBeIcon";
import HelpIcon from "../../../icons/HelpIcon";
import LogoutIcon from "../../../icons/LogoutIcon";

// Utils
import { clientWidth } from "../../../utils";

// Images
import defaultPartnerPhoto from "../../../images/generic-trainerphoto.svg";

const BundleBlock = ({ bundle }) => {
  const hasBundle = !!bundle;

  if (hasBundle) {
    let partnerProfilePictureUrl;
    let bundleBars = [];
    let sessionsLeft = bundle.sessionsCount - bundle.sessionsUsed;
    let hasSessions = sessionsLeft > 0;

    for (let i = 0; i < bundle.sessionsCount; i++) {
      let className = `tbk-mr-[2px] tbk-h-0.5 tbk-grow ${
        i === 0
          ? "tbk-rounded-l-lg"
          : i === bundle.sessionsCount - 1
          ? "tbk-rounded-r-lg"
          : ""
      } ${
        i >= sessionsLeft
          ? "tbk-bg-grey-inactive"
          : bundle.type === "LS"
          ? "tbk-bg-violet"
          : "tbk-bg-coral"
      }`;

      bundleBars.push(<div className={className} key={i} />);
    }

    partnerProfilePictureUrl =
      bundle.partnerProfilePictureUrls &&
      bundle.partnerProfilePictureUrls.length &&
      bundle.partnerProfilePictureUrls[0];

    return (
      <Link
        to="/bundles"
        className="tbk-mb-2 tbk-block tbk-rounded-lg tbk-bg-grey-light tbk-p-2"
      >
        <div className="tbk-mb-1 tbk-flex tbk-items-center">
          <img
            className="tbk-mr-1 tbk-h-4 tbk-w-4 tbk-shrink-0"
            itemProp="image"
            src={
              partnerProfilePictureUrl
                ? partnerProfilePictureUrl + "?size=128x128"
                : defaultPartnerPhoto
            }
            srcSet={`${
              partnerProfilePictureUrl
                ? partnerProfilePictureUrl + "?size=128x128"
                : defaultPartnerPhoto
            } 1x, ${
              partnerProfilePictureUrl
                ? partnerProfilePictureUrl + "?size=256x256"
                : defaultPartnerPhoto
            } 2x`}
            alt={`${bundle.partnerFullName} photo`}
          />
          <div className="tbk-grow">
            <div className="tbk-text-small tbk-text-grey-main">
              Sessions Bundle with {"Tuesday"}
            </div>
          </div>
        </div>
        <div
          className={`tbk-flex tbk-justify-evenly${
            hasSessions ? "" : " no-sessions"
          }`}
        >
          {bundleBars}
        </div>
      </Link>
    );
  }

  return null;
};

export const Menu = ({
  isNight,
  onMenuOpen,
  onMenuClose,
  onSignUpClick,
  onLoginClick,
  onLogoutClick,
  onViewCoachesLinkClick,
}) => {
  const location = useLocation();

  const { theme } = useTheme();

  const { user, signOut } = useAppState();

  const [isDropdownOpen, setIsDropdownOpen] = useState(); // eslint-disable-line

  const isUserAuthenticated = !!user;
  const userFirstName = user && user.firstName;
  const userFamilyName = user && user.lastName;
  const pictureUrl = user && user.photo;
  const paused = false;

  const handleLogout = useCallback(() => {
    signOut();
  }, []);

  const ButtonElement = ({ pictureUrl, userFirstName, isNight }) => (
    <button className="tb-dropdown-button">
      <div
        className={`tbk-flex tbk-items-center tbk-rounded-lg tbk-border tbk-border-2 ${
          isNight
            ? "tbk-border-transparent"
            : "tbk-border-solid tbk-border-grey-inactive"
        } tbk-px-1 tbk-py-0.5`}
      >
        <div
          className={`tbk-mr-1 ${
            isNight ? "tbk-text-basic-white" : "tbk-text-grey-main"
          }`}
        >
          <MenuBurgerIcon />
        </div>
        <ProfilePicture
          className={`tbk-shrink-0${paused ? " paused" : ""}`}
          pictureUrl={pictureUrl}
        />
        {userFirstName ? (
          <div
            className={`tb-header-menu-profile-text tbk-text-main ${
              isNight ? "tbk-text-basic-white" : "tbk-text-grey-main"
            } tbk-ml-1 tbk-hidden md:tbk-block`}
          >
            {userFirstName}
          </div>
        ) : null}
      </div>
    </button>
  );

  return (
    <div className="tb-header-menu">
      <div className="">
        <Dropdown
          className="tb-dropdown-login"
          active={isDropdownOpen}
          keepOpen={false}
          buttonElement={ButtonElement}
          buttonElementProps={{ pictureUrl, userFirstName, isNight }}
          position={
            clientWidth() < theme.responsive.breakpoints.sm
              ? "fixed"
              : "absolute"
          }
          placement={
            clientWidth() < theme.responsive.breakpoints.sm
              ? "tbk-top-7 tbk-left-0 tbk-right-0"
              : "tbk-mt-0.5 tbk-right-0"
          }
          noBorder
          rounded={
            clientWidth() < theme.responsive.breakpoints.md ? "bottom" : "top"
          }
          // onClick={ e => this.setState({ isLoginMenuOpen: null }) }
          onOpen={onMenuOpen}
          onClose={onMenuClose}
          // onClose={this.context.closeLoginMenu}
        >
          <div className="tbk-w-full tbk-rounded-2xl tbk-bg-basic-white tbk-pt-1 md:tbk-w-80">
            {user ? (
              <div
                className={`tbk-mx-2 tbk-border-b tbk-border-grey-inactive tbk-py-0`}
              >
                <Link
                  className="tbk-flex tbk-items-start tbk-py-2"
                  to="/account"
                >
                  <ProfilePicture
                    className={`tbk-shrink-0${paused ? " paused" : ""}`}
                    pictureUrl={pictureUrl}
                  />
                  <div className="tbk-ml-1 tbk-grow">
                    <div className={"tbk-text-h3-subtitle tbk-text-grey-main"}>
                      {userFirstName} {userFamilyName}
                    </div>
                    <div className="tbk-text-small tbk-text-blue-grey">
                      Show Profile
                    </div>
                  </div>
                  <div className={"tbk-text-grey-inactive"}>
                    <CaretRightIcon />
                  </div>
                </Link>
                {/*<BundleBlock bundle={activeBundle} />*/}
                <Link
                  className="tbk-mb-2 tbk-flex tbk-items-center tbk-py-0.5"
                  to="/account/bookings"
                >
                  <CalendarIcon className="tbk-mr-2 tbk-shrink-0 tbk-text-green" />
                  <div className="tbk-text-h3-subtitle tbk-grow tbk-text-grey-main">
                    My Bookings
                  </div>
                  <div className={"tbk-text-grey-inactive"}>
                    <CaretRightIcon />
                  </div>
                </Link>
              </div>
            ) : null}
            <div className="tbk-px-2 tbk-pb-1.5">
              {(true || isUserAuthenticated) && (
                <Link
                  className="tbk-mt-0.5 tbk-flex tbk-items-center tbk-pt-2 tbk-pb-1.5"
                  to="/partners"
                  onClick={onViewCoachesLinkClick}
                >
                  <div className="tbk-mr-2 tbk-shrink-0 tbk-text-green">
                    {clientWidth() < theme.responsive.breakpoints.md ? (
                      <BoltIcon />
                    ) : (
                      <CupIcon />
                    )}
                  </div>

                  <h4 className="tbk-text-h3-subtitle tbk-m-0 tbk-flex tbk-grow tbk-whitespace-nowrap tbk-text-grey-main">
                    Find Your{" "}
                    <FormattedMessage id="Trainer" defaultMessage="Trainer" />
                  </h4>
                  <div className={"tbk-text-grey-inactive"}>
                    <CaretRightIcon />
                  </div>
                </Link>
              )}
              {/*{
                clientWidth() < theme.responsive.breakpoints.md ? (
                  <Link
                    to="/lifestyle"
                    className="tbk-flex tbk-w-full tbk-items-center tbk-pt-1.5 tbk-pb-1.5"
                  >
                    <div className="tbk-mr-2 tbk-shrink-0 tbk-text-coral">
                      <TruBeIcon />
                    </div>

                    <h4 className="tbk-text-h3-subtitle tbk-m-0 tbk-flex tbk-grow tbk-whitespace-nowrap tbk-text-grey-main">
                      Lifestyle Collective
                    </h4>
                    <div className={"tbk-text-grey-inactive"}>
                      <CaretRightIcon />
                    </div>
                  </Link>
                ) : null
              }*/}
              {!isUserAuthenticated &&
              clientWidth() < theme.responsive.breakpoints.md ? (
                <Link
                  to={{
                    pathname: "/become-a-member",
                    search: "?authState=signUp",
                    state: { from: `${location.pathname}${location.search}` },
                  }}
                  className="tbk-flex tbk-w-full tbk-items-center tbk-pt-1.5 tbk-pb-1.5"
                >
                  <div className="tbk-mr-2 tbk-shrink-0 tbk-text-coral">
                    <UserIcon />
                  </div>

                  <h4 className="tbk-text-h3-subtitle tbk-m-0 tbk-flex tbk-grow tbk-whitespace-nowrap tbk-text-grey-main">
                    Create Account
                  </h4>
                  <div className={"tbk-text-grey-inactive"}>
                    <CaretRightIcon />
                  </div>
                </Link>
              ) : null}
              {!isUserAuthenticated &&
              clientWidth() < theme.responsive.breakpoints.md ? (
                <Link
                  to={{
                    pathname: "/login/",
                    search: "?authState=signUp",
                    state: { from: `${location.pathname}${location.search}` },
                  }}
                  className="tbk-flex tbk-w-full tbk-items-center tbk-pt-1.5 tbk-pb-1.5"
                  onClick={onLoginClick}
                >
                  <div className="tbk-mr-2 tbk-shrink-0 tbk-text-grey-main">
                    <LoginIcon />
                  </div>
                  <h4 className="tbk-text-h3-subtitle tbk-m-0 tbk-flex tbk-grow tbk-whitespace-nowrap tbk-text-grey-main">
                    Log in
                  </h4>
                  <div className={"tbk-text-grey-inactive"}>
                    <CaretRightIcon />
                  </div>
                </Link>
              ) : null}

              {clientWidth() < theme.responsive.breakpoints.md ? (
                <Link
                  className="tbk-flex tbk-items-center tbk-pt-1.5 tbk-pb-1.5"
                  to="/contact"
                >
                  <div className="tbk-mr-2 tbk-shrink-0 tbk-text-grey-main">
                    <SpeechBalloonIcon />
                  </div>

                  <h4 className="tbk-text-h3-subtitle tbk-m-0 tbk-flex tbk-grow tbk-whitespace-nowrap tbk-text-grey-main">
                    Contact Us
                  </h4>
                  <div className={"tbk-text-grey-inactive"}>
                    <CaretRightIcon />
                  </div>
                </Link>
              ) : null}

              <Link
                className="tbk-flex tbk-hidden tbk-items-center tbk-pt-1.5 tbk-pb-1.5"
                to="/contact"
              >
                <div className="tbk-mr-2 tbk-shrink-0 tbk-text-grey-main">
                  <HelpIcon />
                </div>

                <h4 className="tbk-text-h3-subtitle tbk-m-0 tbk-flex tbk-grow tbk-whitespace-nowrap tbk-text-grey-main">
                  Help
                </h4>
                <div className={"tbk-text-grey-inactive"}>
                  <CaretRightIcon />
                </div>
              </Link>

              {isUserAuthenticated ? (
                <button
                  className="tbk-flex tbk-w-full tbk-items-center tbk-pt-1.5 tbk-pb-1.5"
                  onClick={handleLogout}
                >
                  <div className="tbk-mr-2 tbk-shrink-0 tbk-text-grey-main">
                    <LogoutIcon />
                  </div>

                  <h4 className="tbk-text-h3-subtitle tbk-m-0 tbk-flex tbk-grow tbk-whitespace-nowrap tbk-text-grey-main">
                    Log out
                  </h4>
                  <div className={"tbk-text-grey-inactive"}>
                    <CaretRightIcon />
                  </div>
                </button>
              ) : null}
            </div>
          </div>
        </Dropdown>
      </div>
    </div>
  );
};

Menu.displayName = "Menu";

export default Menu;
